"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.stableProbability = stableProbability;
exports.stableWeightedVariant = stableWeightedVariant;
const tslib_1 = require("tslib");
const spark_md5_1 = tslib_1.__importDefault(require("spark-md5"));
/**
 * Given an arbitrary input string, and a probability between 0 and 1, returns true with the specified probability, as a pure
 * function, keeping the same boolean output for the same input string.
 *
 * This is useful for e.g. percentage rollouts, where you want to consistently assign a user to either of two variants
 * based on their ID.
 */
function stableProbability(inputToHash, probability) {
    if (probability < 0 || probability > 1) {
        throw new Error("Probability must be between 0 and 1");
    }
    return stableSelectedRatio(inputToHash, [probability, 1 - probability]) === 0;
}
/**
 * Given an arbitrary input string, and a set of weighted variants (each with a string key and percentage, ratio or
 * probability of being assigned), returns one of the variants, as a pure function, keeping the same variant output for
 * the same input string
 *
 * This is useful for e.g. percentage rollouts, where you want to consistently assign a user to a variant based on their ID
 *
 * You must provide at least one variant, or this function will throw an error. This ensures we can always return a valid
 * variant key in all other cases.
 */
function stableWeightedVariant(inputToHash, variants) {
    const keys = Object.keys(variants);
    const firstKey = keys[0];
    if (firstKey === undefined) {
        throw new Error("No variants provided");
    }
    const group = stableSelectedRatio(inputToHash, Object.values(variants));
    const selectedVariant = keys[group];
    if (!selectedVariant) {
        // This can happen e.g. if a variant has a probability of Infinity, or all the variants have zero probability
        // It doesn't happen for variant configs where at least one variant has non-zero weight, and none are infinite
        throw new Error("No variant selected");
    }
    return selectedVariant;
}
/**
 * Internal implementation of the previous two easier-to-use functions - probably use those instead
 *
 * Given an arbitrary input string, and an array of numbers, returns a number between 0 and the length of the array minus 1,
 * and the number returned is determined by the weighting of the numbers given, in a stable way (depending on the inputHash).
 * The given ratios may be percentages, probabilities, or any other kind of weighting, as long as they sum to a finite number.
 *
 * Note that the provided array of ratios could have a hole, so the returned number can't be used to directly index the
 * ratios array without further assumptions. If only zero or one ratio is provided, returns 0; so further note that in the case
 * where no ratios are provided, the returned number can't be used to index the ratios array at all.
 *
 * Uses 16 bits of randomness, so the probability is limited in precision to 1/65536.
 */
function stableSelectedRatio(inputToHash, ratios) {
    if (ratios.length <= 1) {
        return 0;
    }
    const sumOfProbabilities = ratios.reduce((a, b) => a + b, 0);
    const scalingFactor = (1 / sumOfProbabilities) * 65536;
    const thresholds = ratios.map((p) => p * scalingFactor);
    // A stable "random" value between 0 and 65535
    const uint16 = parseInt(spark_md5_1.default.hash(inputToHash, false).slice(0, 4), 16);
    let threshold = 0;
    let group = -1;
    for (const nextRatio of thresholds) {
        group += 1;
        threshold += nextRatio;
        if (uint16 < threshold) {
            return group;
        }
    }
    return group + 1;
}
//# sourceMappingURL=stableProbability.js.map